import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, Card, CardActionArea, CardContent, CardHeader, Typography, Grid,
  Accordion, AccordionSummary, AccordionDetails, Button, MenuItem, Select, FormControl, InputLabel,
  Tooltip, Avatar
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import PaidIcon from '@mui/icons-material/Paid';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BadgeIcon from '@mui/icons-material/Badge';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import PhoneIcon from '@mui/icons-material/Phone';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import IconButton from '@mui/material/IconButton';
import HistoryIcon from '@mui/icons-material/History';
import HttpsIcon from '@mui/icons-material/Https';
import { Space } from 'antd';
import { message, ConfigProvider, DatePicker, Input } from 'antd';
import esES from 'antd/es/locale/es_ES';
import moment from 'moment';
import 'moment/locale/es';

moment.locale('es');

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return '0.00';
  }
  return value.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formattedNumber(value) {
  let number = parseNumber(value);
  return formatCurrency(number);
}

function ViewInvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]); // Store all invoices for filtering
  const [messageApi, contextHolder] = message.useMessage();
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // Nuevo estado para el filtro de estado de pago
  const [loading, setLoading] = useState(true);
  const [initializedFromURL, setInitializedFromURL] = useState(false);
  const [permissionError, setPermissionError] = useState(false);

  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const location = useLocation();

  const getInvoices = async () => {
    try {
      const response = await fetch(`https://dprincess-training.servehttp.com/invoices/list-invoices-details`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();

        for (const invoice of data) {
          let paidAmount = 0;
          let reservationPaid = [];
          for (const details of invoice.invoiceDetails) {
            paidAmount += parseNumber(details.amount);
            if (details.serviceID === 24) {
              details.amount = parseNumber(details.amount);
              reservationPaid.push(details);
            }
          }
          invoice.pendingAmount = parseNumber(invoice.totalAmount) - paidAmount;
          invoice.paidAmount = paidAmount;
          invoice.reservationPaid = reservationPaid;
        }

        setInvoices(data);
        setAllInvoices(data); // Store all invoices for filtering
        setLoading(false);
      } else if (response.status === 403) {
        setPermissionError(true);
        setLoading(false);
      } else {
        messageApi.open({
          type: 'error',
          content: 'Error al cargar las facturas pendientes, recargue la página.',
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.open({
        type: 'error',
        content: 'Error al cargar las facturas pendientes, recargue la página.',
      });
      setLoading(false);
    }
  }

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  // Handle payment status change
  const handlePaymentStatusChange = (event) => {
    setPaymentStatus(event.target.value);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      // Set time to beginning of first day and end of last day for proper range comparison
      const startDate = moment(dates[0]).startOf('day');
      const endDate = moment(dates[1]).endOf('day');
      setDateRange([startDate, endDate]);
    } else {
      setDateRange(null);
    }
  };

  // Apply filters to invoices
  const applyFilters = useCallback(() => {
    let filteredInvoices = [...allInvoices];

    // Filter by payment status
    if (paymentStatus) {
      filteredInvoices = filteredInvoices.filter(
        invoice => invoice.paymentStatus.toLowerCase() === paymentStatus
      );
    }

    // Filter by search term (invoice number, client name, or cellphone)
    if (search.trim()) {
      const searchTerm = search.toLowerCase();
      filteredInvoices = filteredInvoices.filter(
        invoice =>
          invoice.invoiceNumber?.toString().toLowerCase().includes(searchTerm) ||
          invoice.clientName?.toLowerCase().includes(searchTerm) ||
          invoice.cellphoneNumber?.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by date range
    if (dateRange && dateRange.length === 2) {
      filteredInvoices = filteredInvoices.filter(invoice => {
        if (!invoice.invoiceDate) return false;

        // Parse the invoice date string to a moment object
        const invoiceDate = moment(invoice.invoiceDate);
        const startDate = dateRange[0];
        const endDate = dateRange[1];

        // For inclusive range, ensure we get complete days
        const isInRange = (
          invoiceDate.isSameOrAfter(startDate, 'day') &&
          invoiceDate.isSameOrBefore(endDate, 'day')
        );

        return isInRange;
      });
    }

    setInvoices(filteredInvoices);
  }, [allInvoices, search, paymentStatus, dateRange]);

  useEffect(() => {
    getInvoices();
  }, []);

  useEffect(() => {
    if (allInvoices.length > 0) {
      applyFilters();
    }
  }, [search, paymentStatus, dateRange, allInvoices, applyFilters]);

  useEffect(() => {
    if (initializedFromURL) return;
    const params = new URLSearchParams(location.search);
    let didInit = false;

    const q = params.get('search') || params.get('q');
    if (q) { setSearch(q); didInit = true; }

    const start = params.get('start') || params.get('from');
    const end = params.get('end') || params.get('to');
    if (start && end) {
      const mStart = moment(start, [moment.ISO_8601, 'YYYY-MM-DD'], true);
      const mEnd = moment(end, [moment.ISO_8601, 'YYYY-MM-DD'], true);
      if (mStart.isValid() && mEnd.isValid()) {
        const ordered = mStart.isAfter(mEnd) ? [mEnd, mStart] : [mStart, mEnd];
        setDateRange(ordered);
        didInit = true;
      }
    }

    const status = params.get('status');
    if (status && ['pendiente', 'pagado'].includes(status.toLowerCase())) {
      setPaymentStatus(status.toLowerCase());
      didInit = true;
    }

    setInitializedFromURL(true);
    if (didInit) {
      // applyFilters se disparará por efecto existente
    }
  }, [location.search, initializedFromURL]);

  useEffect(() => {
    if (!initializedFromURL) return;
    const params = new URLSearchParams(location.search);

    if (search) params.set('search', search); else params.delete('search');

    if (dateRange && dateRange.length === 2) {
      params.set('start', dateRange[0].format('YYYY-MM-DD'));
      params.set('end', dateRange[1].format('YYYY-MM-DD'));
    } else {
      params.delete('start');
      params.delete('end');
    }

    if (paymentStatus) params.set('status', paymentStatus); else params.delete('status');

    const newSearch = params.toString();
    const target = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    if (target !== `${location.pathname}${location.search}`) {
      navigate(target, { replace: true });
    }
  }, [search, dateRange, paymentStatus, initializedFromURL]);

  if (loading) {
    return (
      <Container sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '90vh',
        background: '#f3e8ff'
      }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 4,
            borderRadius: '16px',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px 0 rgba(247,89,171,0.18)',
            border: '1.5px solid #f759ab',
            maxWidth: '400px',
            width: '100%'
          }}
        >
          <Box sx={{ position: 'relative', mb: 3 }}>
            <ReceiptLongIcon
              sx={{
                color: '#f759ab',
                fontSize: 60,
                animation: 'pulse 1.5s infinite ease-in-out',
                '@keyframes pulse': {
                  '0%': { opacity: 0.6, transform: 'scale(0.95)' },
                  '50%': { opacity: 1, transform: 'scale(1.05)' },
                  '100%': { opacity: 0.6, transform: 'scale(0.95)' }
                }
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                animation: 'spin 2s infinite linear',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            >
              <Box
                sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  border: '3px solid transparent',
                  borderTop: '3px solid #4b0082',
                  borderRight: '3px solid #4b0082'
                }}
              />
            </Box>
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: '#4b0082',
              fontWeight: 700,
              mb: 1,
              textAlign: 'center'
            }}
          >
            Cargando facturas
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                sx={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#f759ab',
                  borderRadius: '50%',
                  mx: 0.5,
                  animation: 'bounce 1.4s infinite ease-in-out both',
                  animationDelay: `${dot * 0.16}s`,
                  '@keyframes bounce': {
                    '0%, 80%, 100%': { transform: 'scale(0)' },
                    '40%': { transform: 'scale(1)' }
                  }
                }}
              />
            ))}
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: '#7c3aed',
              mt: 2,
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Estamos solicitando la información de tus facturas
          </Typography>
        </Box>
      </Container>
    );
  };

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <div style={{ background: '#f3e8ff', minHeight: '90vh', overflow: 'auto' }}>
      <Container
        sx={{
          padding: '32px 0',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
        }}
        maxWidth="lg"
      >
        {contextHolder}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '90%' }}>
          <Typography
            variant="h4"
            sx={{
              marginBottom: '28px',
              color: '#4b0082',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textAlign: 'center',
            }}
          >
            Registro de Facturación
          </Typography>

          {/* Filter Section */}
          <Card sx={{ width: '100%', mb: 4, borderRadius: '18px', border: '1.5px solid #f759ab' }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: '#4b0082',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <SearchIcon sx={{ mr: 1, color: '#f759ab' }} />
                Filtrar facturas
              </Typography>
              <Grid container spacing={2}>
                {/* Search Input */}
                <Grid item xs={12} md={4}>
                  <ConfigProvider theme={{ token: { colorPrimary: '#f759ab' } }}>
                    <Input
                      placeholder="Buscar por número, cliente o celular"
                      value={search}
                      onChange={handleSearchChange}
                      allowClear
                      size="large"
                    />
                  </ConfigProvider>
                </Grid>

                {/* Date Range Picker */}
                <Grid item xs={12} md={5}>
                  <ConfigProvider locale={esES} theme={{ token: { colorPrimary: '#f759ab' } }}>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        onChange={handleDateRangeChange}
                        format="DD/MM/YYYY"
                        placeholder={['Fecha inicio', 'Fecha fin']}
                        allowClear={true}
                        size="large"
                        ranges={{
                          'Este Mes': [moment().startOf('month'), moment().endOf('month')],
                          'Mes Anterior': [moment().subtract(1, 'months').startOf('month'), moment().subtract(1, 'months').endOf('month')],
                          'Últimos 30 Días': [moment().subtract(29, 'days'), moment()]
                        }}
                      />
                    </Space>
                  </ConfigProvider>
                </Grid>

                {/* Payment Status Filter */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="payment-status-select-label"
                      sx={{ color: '#4b0082' }}>
                      Estado de pago
                    </InputLabel>
                    <Select
                      labelId="payment-status-select-label"
                      id="payment-status-select-label"
                      value={paymentStatus}
                      onChange={handlePaymentStatusChange}
                      label="Estado de pago"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f759ab',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f759ab',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f759ab',
                        }
                      }}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="pagado">Pagado</MenuItem>
                      <MenuItem value="pendiente">Pendiente</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Existing invoices list rendering */}
          {invoices.length > 0 ? invoices.map((invoice, index) => (
            <Grid container spacing={1} key={index} sx={{ marginBottom: '24px', justifyContent: 'center' }}>
              <Grid item xs={12} md={10}>
                <Card
                  sx={{
                    width: "100%",
                    borderRadius: '18px',
                    border: '1.5px solid #f759ab',
                    boxShadow: '0 4px 16px 0 rgba(247,89,171,0.10)',
                    background: 'linear-gradient(90deg, #fff 80%, #f3e8ff 100%)',
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 32px 0 rgba(247,89,171,0.18)',
                      borderColor: '#f759ab'
                    }
                  }}
                >
                  {/* Header */}
                  <CardHeader
                    title={
                      <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}>
                        <Grid item sm={6} xs={10} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 1 }}>
                            <ReceiptLongIcon sx={{ color: '#f759ab', fontSize: 32, mr: 1 }} />
                            <Box>
                              <Typography
                                sx={{
                                  color: '#4b0082',
                                  fontWeight: 700,
                                  fontSize: { xs: '1rem', sm: '1.15rem' },
                                  m: 0,
                                  lineHeight: { xs: 1.05, sm: 1.2 }
                                }}
                                component="div"
                              >
                                Facturado a {invoice.clientName}
                              </Typography>
                              <Typography
                                sx={{
                                  color: '#7c3aed',
                                  fontWeight: 500,
                                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                  mt: 0.5,
                                  lineHeight: { xs: 1.05, ms: 1.2 }
                                }}
                                component="div"
                              >
                                Número de facturación: <span style={{ fontWeight: 700 }}>{invoice.invoiceNumber}</span>
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item sm={6} xs={2} sx={{ textAlign: 'right' }}>
                          <Typography
                            sx={{
                              color: '#7c3aed',
                              fontWeight: 700,
                              fontSize: { xs: '0.95rem', sm: '1.15rem' },
                              m: 0,
                              lineHeight: { xs: 1, sm: 1.2 }
                            }}
                            component="div"
                          >
                            <IconButton onClick={() => navigate(`/billing/invoice/${invoice.invoiceID}`)}>
                              <Tooltip title="Gestionar factura" arrow>
                                <ViewListIcon sx={{ color: '#7c3aed', fontSize: 26 }} />
                              </Tooltip>
                            </IconButton>
                          </Typography>
                        </Grid>
                      </Grid>
                    }
                    sx={{ pb: 0, pt: 2, px: 3, background: '#f8f0fc', borderTopLeftRadius: 18, borderTopRightRadius: 18 }}
                  />
                  {/* Rest of the card content */}
                  <CardContent sx={{ pt: 2, pb: 2, px: { xs: 1, sm: 3 } }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <PersonIcon sx={{ color: '#f759ab', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Cliente: <span style={{ color: '#4b0082', fontWeight: 700 }}>{invoice.clientName}</span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <SmartphoneIcon sx={{ color: '#f759ab', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Celular: <span style={{ color: '#4b0082', fontWeight: 700 }}>{invoice.cellphoneNumber}</span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <PhoneIcon sx={{ color: '#f759ab', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Teléfono: <span style={{ color: '#4b0082', fontWeight: 700 }}>{invoice.phoneNumber}</span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <BadgeIcon sx={{ color: '#f759ab', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Atendido por: <span style={{ color: '#4b0082', fontWeight: 700 }}>{invoice.employeeName}</span>
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {/* Existing invoice data displays... */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <ReceiptIcon sx={{ color: '#f759ab', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Total facturado: <span style={{ color: '#4b0082', fontWeight: 700 }}>{formatCurrency(parseNumber(invoice.totalAmount))}</span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <PaidIcon sx={{ color: '#22c55e', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Total pagado: <span style={{ color: '#4b0082', fontWeight: 700 }}>{formatCurrency(parseNumber(invoice.paidAmount))}</span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <ReceiptIcon sx={{ color: '#f759ab', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Pendiente por pagar: <span style={{ color: '#d97706', fontWeight: 700 }}>{formatCurrency(parseNumber(invoice.pendingAmount))}</span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <EventNoteIcon sx={{ color: '#f759ab', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Fecha de facturación: <span style={{ color: '#4b0082', fontWeight: 700 }}>
                              {invoice.invoiceDate
                                ? new Date(invoice.invoiceDate).toLocaleString('es-DO', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                })
                                : 'No registrada'}
                            </span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          {
                            invoice.paymentStatus === "pagado" ? (
                              <PaidIcon sx={{ color: '#22c55e', mr: 1 }} />
                            ) : (
                              <RequestQuoteIcon sx={{ color: '#d97706', mr: 1 }} />
                            )
                          }
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Estado de pago: <span style={{ color: invoice.paymentStatus === "pagado" ? "green" : "#d97706", fontWeight: 700 }}>{invoice.paymentStatus.toUpperCase()}</span>
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ width: '100%' }}>
                      {/* Existing accordion section */}
                      <Accordion sx={{ borderRadius: '8px', width: '100%' }}>
                        <CardActionArea
                          sx={{
                            mt: 3,
                            background: '#f759ab',
                            color: '#fff',
                            transition: 'background 0.2s',
                            '&:hover': { background: '#e04897' },
                            boxShadow: '0 2px 8px 0 rgba(247,89,171,0.10)'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ArrowDropDownIcon sx={{ fontWeight: 700, margin: 0, padding: 0, color: '#fff' }} />}
                            aria-controls="panel2-content"
                            id="panel2-header"
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 0, m: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, margin: 0, padding: 0 }}>
                                Ver historial de pagos de la factura
                              </Typography>
                            </Box>
                          </AccordionSummary>
                        </CardActionArea>
                        <AccordionDetails sx={{ padding: 0, margin: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', py: 1, flexDirection: 'column', width: '100%' }}>
                            <PaymentHistory invoice={invoice} index={index} />
                            {
                              invoice.reservationPaid && invoice.reservationPaid.length > 0 && (
                                <Button sx={{ mt: 2, background: '#f3e8ff', color: '#4b0082', borderRadius: '8px', padding: '8px 16px', '&:hover': { background: '#e9d5ff' } }}
                                  onClick={() => navigate(`/events/view-event-details/${invoice.eventID}`)}>
                                  <EventNoteIcon sx={{ color: '#4b0082', mr: 1 }} />
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#4b0082' }}>
                                    Ver detalles del evento
                                  </Typography>
                                </Button>
                              )
                            }
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )) : (
            <Typography variant="h6" sx={{ color: '#4b0082', mt: 4 }}>
              No hay facturas que coincidan con los filtros aplicados.
            </Typography>
          )}
        </Box>
      </Container>
    </div>
  )
};

function PaymentHistory({ invoice, index }) {
  const invoiceHistory = (invoice) => {
    let array = [];

    // Validar que invoiceDetails existe y es un array
    if (invoice.invoiceDetails && Array.isArray(invoice.invoiceDetails)) {
      for (let i = 0; i < invoice.invoiceDetails.length; i++) {
        const element = invoice.invoiceDetails[i];
        array.push(element);
      }
    }

    // Ordenar por creationDate de forma descendente (más reciente primero)
    return array.sort((a, b) => {
      const dateA = new Date(a.creationDate || a.createdAt || 0);
      const dateB = new Date(b.creationDate || b.createdAt || 0);
      return dateB - dateA; // Orden descendente
    });
  }

  return (
    <Container sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        <Typography
          variant="h5"
          sx={{
            marginBottom: '28px',
            color: '#4b0082',
            fontWeight: 700,
            letterSpacing: '0.5px',
            textAlign: 'center',
            marginTop: '28px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <HistoryIcon sx={{ color: '#4b0082', mr: 1 }} />
          Historial de pagos
        </Typography>

        <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', borderRadius: 2, p: 2 }}>
          {/* Payment history rendering con validación mejorada */}
          {
            (() => {
              const history = invoiceHistory(invoice);
              return history.length > 0 ? (
                history.map((payment, idx) => (
                  <Grid container spacing={2} key={idx} sx={{ mb: 2, p: { xs: 0.5, sm: 1.5 } }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                        Fecha de pago: <span style={{ color: '#4b0082', fontWeight: 700 }}>
                          {payment.paymentDate
                            ? new Date(payment.paymentDate).toLocaleString('es-DO')
                            : payment.creationDate
                              ? new Date(payment.creationDate).toLocaleString('es-DO')
                              : 'No disponible'
                          }
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                        Monto pagado: <span style={{ color: '#4b0082', fontWeight: 700 }}>{formattedNumber(payment.amount || 0)}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                        Método de pago: <span style={{ color: '#4b0082', fontWeight: 700 }}>{payment.paymentMethod?.toUpperCase() || 'No especificado'}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                        Servicio: <span style={{ color: '#4b0082', fontWeight: 700 }}>
                          {(payment.serviceName)}
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                        Concepto: <span style={{ color: '#4b0082', fontWeight: 700 }}>{payment.description || 'No disponible'}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                        Atendido por: <span style={{ color: '#4b0082', fontWeight: 700 }}>{payment.employeeName || 'No disponible'}</span>
                      </Typography>
                    </Grid>
                  </Grid>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: '#d97706', mt: 2 }}>
                  No hay pagos registrados para esta factura.
                </Typography>
              );
            })()
          }
        </Box>
      </Box>
    </Container>
  );
}

function ItemDetails({ invoice, index }) {
  return (
    <Container sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Typography
          variant="h5"
          sx={{
            marginBottom: '28px',
            color: '#4b0082',
            fontWeight: 700,
            letterSpacing: '0.5px',
            textAlign: 'center',
            marginTop: '28px'
          }}
        >
          Detalles de la factura
        </Typography>
        {/* Invoice details rendering */}
        {/* Reservation details rendering */}
        {
          invoice.reservationPaid && invoice.reservationPaid.length > 0 && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Servicio: <span style={{ color: '#4b0082', fontWeight: 700 }}>{invoice.comboName}</span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Precio: <span style={{ color: '#4b0082', fontWeight: 700 }}>{formattedNumber(invoice.comboPrice)}</span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Cantidad: <span style={{ color: '#4b0082', fontWeight: 700 }}>1</span>
                </Typography>
              </Grid>
              {invoice.reservationPaid && invoice.reservationPaid?.length > 0 && invoice.reservationPaid[0]?.serviceID === 24 &&
                (<Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                    Total pagado: <span style={{ color: '#4b0082', fontWeight: 700 }}>
                      {invoice.reservationPaid && invoice.reservationPaid.length > 0
                        ? invoice.reservationPaid.length === 1
                          ? `${formattedNumber(invoice.reservationPaid[0].amount)}`
                          : `${formattedNumber(invoice.reservationPaid.reduce((sum, payment) => sum + parseNumber(payment.amount), 0))}`
                        : 'RD$0.00'
                      }
                    </span>
                  </Typography>
                </Grid>)}
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Pendiente por pagar: <span style={{ color: '#4b0082', fontWeight: 700 }}>{formattedNumber(parseNumber(invoice.comboPrice) - invoice.reservationPaid.reduce((sum, payment) => sum + parseNumber(payment.amount), 0))}</span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Estado de pago: <span style={{ color: '#d97706', fontWeight: 700 }}>{invoice.reservationPaid[0].paymentStatus.toUpperCase()}</span>
                </Typography>
              </Grid>
            </Grid>
          )
        }
        {/* Service details rendering */}
        {
          invoice.invoiceDetails && invoice.invoiceDetails.length > 0 && invoice.invoiceDetails.map((detail, index) => detail.serviceID !== 24 && (
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Servicio: <span style={{ color: '#4b0082', fontWeight: 700 }}>{detail.serviceName}</span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Precio: <span style={{ color: '#4b0082', fontWeight: 700 }}>{
                    (detail.servicePrice > 0
                      ? formattedNumber(parseNumber(detail.servicePrice) * parseNumber(detail.serviceQty) * (1 - (detail.discountApplied ? detail.discountApplied / 100 : 0)))
                      : formattedNumber((parseNumber(detail.price)) * parseNumber(detail.serviceQty) * (1 - (detail.discountApplied ? detail.discountApplied / 100 : 0))))}</span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Cantidad: <span style={{ color: '#4b0082', fontWeight: 700 }}>{detail.serviceQty}</span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Total pagado: <span style={{ color: '#4b0082', fontWeight: 700 }}>{formattedNumber(detail.amount)}</span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Pendiente por pagar: <span style={{ color: '#4b0082', fontWeight: 700 }}>
                    {formattedNumber(((detail.servicePrice > 0 ? formattedNumber(detail.servicePrice) : parseNumber(detail.price)) * parseNumber(detail.serviceQty)) - parseNumber(detail.amount))}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  Estado de pago: <span style={{ color: detail.paymentStatus === "pagado" ? "green" : "#d97706", fontWeight: 700 }}>{detail.paymentStatus.toUpperCase()}</span>
                </Typography>
              </Grid>
            </Grid>
          ))
        }
      </Box>
    </Container>
  )
}

export default ViewInvoicesList